<template>
    <div class="embla-wrapper">
        <section class="embla">
            <div ref="emblaRef" class="embla__viewport">
                <div class="embla__container">
                    <div v-for="(text, i) in texts" :key="i" class="embla__slide">
                        <div class="embla__slide__number">
                            {{ text }}
                        </div>
                    </div>
                </div>
            </div>
            <div class="embla__controls">
                <div class="embla__buttons">
                    <button
                        @click="scrollPrev"
                        @mouseenter="stopAutoplay"
                        @mouseleave="playAutoplay"
                        class="embla__button embla__button--prev"
                        type="button"
                    >
                        <svg class="embla__button__svg" viewBox="0 0 532 532">
                            <path
                                fill="currentColor"
                                d="M355.66 11.354c13.793-13.805 36.208-13.805 50.001 0 13.785 13.804 13.785 36.238 0 50.034L201.22 266l204.442 204.61c13.785 13.805 13.785 36.239 0 50.044-13.793 13.796-36.208 13.796-50.002 0a5994246.277 5994246.277 0 0 0-229.332-229.454 35.065 35.065 0 0 1-10.326-25.126c0-9.2 3.393-18.26 10.326-25.2C172.192 194.973 332.731 34.31 355.66 11.354Z"
                            ></path>
                        </svg>
                    </button>
                    <button
                        @click="scrollNext"
                        @mouseenter="stopAutoplay"
                        @mouseleave="playAutoplay"
                        class="embla__button embla__button--next"
                        type="button"
                    >
                        <svg class="embla__button__svg" viewBox="0 0 532 532">
                            <path
                                fill="currentColor"
                                d="M176.34 520.646c-13.793 13.805-36.208 13.805-50.001 0-13.785-13.804-13.785-36.238 0-50.034L330.78 266 126.34 61.391c-13.785-13.805-13.785-36.239 0-50.044 13.793-13.796 36.208-13.796 50.002 0 22.928 22.947 206.395 206.507 229.332 229.454a35.065 35.065 0 0 1 10.326 25.126c0 9.2-3.393 18.26-10.326 25.2-45.865 45.901-206.404 206.564-229.332 229.52Z"
                            ></path>
                        </svg>
                    </button>
                </div>
                <div class="embla__dots">
                    <button
                        v-for="i in slideCount"
                        :key="i"
                        class="embla__dot"
                        :class="{ 'embla__dot--selected': currentIndex === i - 1 }"
                        type="button"
                        @click="scrollTo(i - 1)"
                    ></button>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import EmblaCarousel from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";

export default {
    name: "EmblaCarousel",
    data() {
        return {
            emblaApi: null,
            remainingTime: null, // Start as null for paused state
            intervalId: null,
            autoPlayDelay: 5000,
            currentIndex: 0,
            slideCount: 0,
        };
    },
    computed: {
        texts() {
            return [
                `"Before we get started, I'd like to ensure you're with the right person. Were you referred to a specific loan officer here at Ruoff?"`,
                `"To make your experience as smooth as possible, have you previously worked with one of our loan officers, or were you recommended to a particular one?"`,
                `"Just to confirm, did someone suggest you work with a particular loan officer from our team? We value those relationships and want to honor them."`,
                `"To provide you with the best service, may I check if you were directed to work with someone specific here? If so, I'll make sure you're linked up with them."`,
                `"To ensure continuity and the best service, were you referred to one of our loan officers by name? We're committed to maintaining those connections."`,
                `"I want to make your process as seamless as possible. Were you expecting to work with someone specific from our team? Please let me know so I can assist you accordingly."`,
                `"For the best service experience, can you confirm if you were referred by someone recommending a particular loan officer? We strive to match you with the right person."`,
            ];
        },
    },
    mounted() {
        // Calculate scrollbar width and set CSS variable
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.documentElement.style.setProperty("--scrollbar-width", `${scrollbarWidth}px`);

        const emblaRef = this.$refs.emblaRef;

        this.emblaApi = EmblaCarousel(
            emblaRef,
            {
                skipSnaps: false,
                align: "start",
                loop: true,
            },
            [
                Autoplay({
                    delay: this.autoPlayDelay,
                    stopOnMouseEnter: true,
                    playOnInit: false, // Key: Prevent auto-start before listeners
                    stopOnInteraction: false, // Key: Auto-resume after manual scrolls (e.g., buttons)
                }),
            ]
        );

        const autoplay = this.emblaApi.plugins().autoplay;

        // Listen for timer events to sync reactive remaining time
        this.emblaApi.on("autoplay:timerset", () => {
            const time = autoplay.timeUntilNext();
            this.remainingTime = time !== undefined && time !== null ? time : null;
            if (this.intervalId) clearInterval(this.intervalId);
            this.intervalId = setInterval(() => {
                const intervalTime = autoplay.timeUntilNext();
                this.remainingTime =
                    intervalTime !== undefined && intervalTime !== null ? intervalTime : null;
            }, 16); // ~60fps for smoother, less CPU than 10ms
        });

        this.emblaApi.on("autoplay:timerstopped", () => {
            this.remainingTime = null;
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
        });

        this.emblaApi.on("select", () => {
            this.currentIndex = this.emblaApi.selectedScrollSnap();
        });

        this.slideCount = this.texts.length;

        // Manually start after listeners are set (ensures events are caught)
        autoplay.play();
    },
    beforeDestroy() {
        // Use beforeDestroy for Vue 2 compatibility; swap to beforeUnmount if Vue 3
        if (this.intervalId) {
            clearInterval(this.intervalId);
            this.intervalId = null;
        }
        this.emblaApi?.off("autoplay:timerset");
        this.emblaApi?.off("autoplay:timerstopped");
        this.emblaApi?.off("select");
    },
    methods: {
        scrollPrev() {
            this.emblaApi?.scrollPrev();
        },
        scrollNext() {
            this.emblaApi?.scrollNext();
        },
        scrollTo(index) {
            this.emblaApi?.scrollTo(index);
        },
        stopAutoplay() {
            this.emblaApi?.plugins().autoplay.stop();
        },
        playAutoplay() {
            this.emblaApi?.plugins().autoplay.play();
        },
    },
};
</script>

<style lang="scss" scoped>
.embla {
    max-width: 80rem;
    @include breakpoint(tablet-land) {
        padding-left: 1rem;
        padding-right: 1rem;
        max-width: calc(100dvw - var(--scrollbar-width, 0px) - 2rem);
        box-sizing: border-box;
    }
    margin: 1rem auto;
    --slide-height: 19rem;
    --slide-spacing: 1rem;
    --slide-size: 100%;
    --text-body: #333;
    --text-high-contrast: #fff;
    --detail-medium-contrast: #ccc;
    --detail-high-contrast: #eee;
    --text-high-contrast-rgb-value: 255, 255, 255;
}

.embla__viewport {
    overflow: hidden;
    @include breakpoint(tablet-land) {
        max-width: calc(100dvw - var(--scrollbar-width, 0px));
    }
    height: var(--slide-height);
}

.embla__container {
    display: flex;
    @include breakpoint(tablet-land) {
        max-width: calc(100dvw - var(--scrollbar-width, 0px));
    }
    touch-action: pan-y pinch-zoom;
    margin-left: calc(var(--slide-spacing) * -1);
    height: 100%;
}

.embla__slide {
    transform: translate3d(0, 0, 0);
    flex: 0 0 var(--slide-size);
    min-width: 0;
    padding-left: var(--slide-spacing);
    height: 100%;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.embla__slide__number {
    border-radius: 8px;
    font-size: 2rem;
    @include breakpoint(tablet-port) {
        font-size: 1.5rem;
    }
    text-wrap: pretty;
    font-weight: 400;
    color: var(--text-body);
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
    padding: 2rem;
    box-sizing: border-box;
    text-align: center;
    line-height: 1.4;
    user-select: none;
}

.embla__controls {
    display: flex;
    justify-content: space-between;
    @include breakpoint(tablet-port) {
        justify-content: center;
    }
    align-items: center;
    flex-wrap: wrap-reverse;
    column-gap: 3rem;
    row-gap: 1.2rem;
    margin-top: 1.8rem;
}

.embla__buttons {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.6rem;
    align-items: center;
}

.embla__button {
    -webkit-tap-highlight-color: rgba(var(--text-high-contrast-rgb-value), 0.5);
    -webkit-appearance: none;
    appearance: none;
    background-color: transparent;
    touch-action: manipulation;
    display: inline-flex;
    text-decoration: none;
    cursor: pointer;
    border: 0;
    padding: 0;
    margin: 0;
    box-shadow: inset 0 0 0 0.2rem var(--detail-medium-contrast);
    width: 3.6rem;
    height: 3.6rem;
    z-index: 1;
    border-radius: 50%;
    color: var(--text-body);
    align-items: center;
    justify-content: center;
    transition: box-shadow 0.2s ease;
}

.embla__button:hover {
    box-shadow: inset 0 0 0 0.2rem var(--text-body);
}

.embla__button:disabled {
    color: var(--detail-high-contrast);
}

.embla__button__svg {
    width: 35%;
    height: 35%;
}

.embla__dots {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-items: center;
    margin-right: calc((2.6rem - 1.4rem) / 2 * -1);
}

.embla__dot {
    -webkit-tap-highlight-color: rgba(var(--text-high-contrast-rgb-value), 0.5);
    -webkit-appearance: none;
    appearance: none;
    background-color: transparent;
    touch-action: manipulation;
    display: inline-flex;
    text-decoration: none;
    cursor: pointer;
    border: 0;
    padding: 0;
    margin: 0;
    width: 2.6rem;
    height: 2.6rem;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: box-shadow 0.2s ease;
}

.embla__dot:after {
    box-shadow: inset 0 0 0 0.2rem var(--detail-medium-contrast);
    width: 1.4rem;
    height: 1.4rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    content: "";
}

.embla__dot:hover:after {
    box-shadow: inset 0 0 0 0.2rem var(--text-body);
}

.embla__dot--selected:after {
    box-shadow: inset 0 0 0 0.2rem var(--text-body);
}
</style>
